import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin, Mail, Building2 } from "lucide-react";
import { HeroFade, Reveal } from "../components/Motion";
import { EMAIL, ADDRESS_LINES } from "../constants";

const CONTACT_IMG =
  "https://images.unsplash.com/photo-1544717297-fa95b6ee9643?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMHdvcmtpbmclMjBwcmVtaXVtfGVufDB8fHx8MTc4NzkzMTk4Mnww&ixlib=rb-4.1.0&q=85";

export default function Contact() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <main data-testid="contact-page">
      <section className="relative overflow-hidden px-6 pt-36 md:px-10 md:pt-44">
        <div className="pointer-events-none absolute -left-40 -top-40 h-[480px] w-[480px] rounded-full bg-blue-100/60 blur-3xl" />
        <div className="relative mx-auto max-w-7xl pb-20">
          <HeroFade delay={0.1}>
            <p className="mb-6 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
              Contact — EY Tech LLC
            </p>
          </HeroFade>
          <h1 data-testid="contact-heading" className="max-w-3xl font-serif text-5xl font-medium leading-[1.05] tracking-tight text-slate-900 md:text-7xl">
            <HeroFade delay={0.2}>Talk to</HeroFade>
            <HeroFade delay={0.35}>
              <em className="text-blue-600">a human.</em>
            </HeroFade>
          </h1>

          <div className="mt-20 grid grid-cols-1 gap-10 md:grid-cols-5">
            <Reveal className="md:col-span-3">
              <div className="h-full rounded-2xl bg-white p-8 shadow-soft md:p-12">
                <div className="mb-6 flex items-center gap-2.5 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
                  <Mail className="h-4 w-4" />
                  Email — fastest
                </div>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="contact-email-link"
                  className="group inline-flex flex-wrap items-center gap-3 font-serif text-2xl font-medium tracking-tight text-slate-900 transition-colors duration-300 hover:text-blue-600 sm:text-3xl md:text-4xl"
                >
                  <span className="break-all">{EMAIL}</span>
                  <ArrowUpRight className="h-7 w-7 shrink-0 text-blue-600 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
                <p className="mt-8 max-w-md text-sm leading-relaxed text-slate-600 md:text-base">
                  Paid for a report? Send your website address here and the clock
                  starts. Questions before you pay? Same address. A person reads
                  every message.
                </p>
              </div>
            </Reveal>

            <div className="flex flex-col gap-10 md:col-span-2">
              <Reveal delay={0.15}>
                <div className="rounded-2xl bg-white p-8 shadow-soft">
                  <div className="mb-4 flex items-center gap-2.5 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
                    <Building2 className="h-4 w-4" />
                    Company
                  </div>
                  <p data-testid="contact-company-name" className="font-serif text-2xl font-medium tracking-tight text-slate-900">
                    EY TECH LLC
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.25}>
                <div className="rounded-2xl bg-white p-8 shadow-soft">
                  <div className="mb-4 flex items-center gap-2.5 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
                    <MapPin className="h-4 w-4" />
                    Registered Office
                  </div>
                  <address data-testid="contact-address" className="text-sm not-italic leading-loose text-slate-600 md:text-base">
                    {ADDRESS_LINES.slice(1).map((line) => (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ))}
                  </address>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section ref={imgRef} className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <div className="relative h-[50vh] overflow-hidden rounded-2xl shadow-lift md:h-[65vh]">
          <motion.img
            src={CONTACT_IMG}
            alt="Business professional at work"
            data-testid="contact-parallax-image"
            style={{ y }}
            className="absolute inset-0 h-[116%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 rounded-full bg-white/90 px-5 py-2.5 text-xs font-semibold text-slate-700 backdrop-blur-sm">
            Real people. Real reports. — Fig. 03
          </div>
        </div>
      </section>
    </main>
  );
}
